import { auth } from './firebase';
import {
  OAuthProvider,
  UserCredential,
  sendPasswordResetEmail,
  signInWithPopup,
  signOut,
  confirmPasswordReset,
} from 'firebase/auth';

// Entra tenant ID (not secret) — restricts the Microsoft popup to our org.
// The popup is served from VITE_FIREBASE_AUTH_DOMAIN, which must match the
// redirect URI registered in the Entra app
// (https://stb.itelligent.ca/__/auth/handler).
const ENTRA_TENANT_ID = 'e3dae33e-dc60-48a6-a637-2f2e9f2e834b';

export async function loginWithMicrosoft(): Promise<UserCredential> {
  const provider = new OAuthProvider('microsoft.com');
  provider.setCustomParameters({ tenant: ENTRA_TENANT_ID });
  provider.addScope('openid');
  provider.addScope('email');
  provider.addScope('profile');
  const result = await signInWithPopup(auth, provider);
  await syncClaims(result);
  return result;
}

// Sends the Microsoft ID token to the API, which validates it, maps Entra
// group memberships to Firebase custom claims, and sets them. Then force a
// token refresh so onIdTokenChanged in App.tsx re-reads the new claims.
// Failures are non-fatal: the user stays signed in, just without claims.
async function syncClaims(result: UserCredential) {
  try {
    const msIdToken = OAuthProvider.credentialFromResult(result)?.idToken;
    if (!msIdToken) {
      console.warn(
        'sync-claims skipped: no Microsoft ID token in the sign-in result'
      );
      return;
    }
    const firebaseToken = await result.user.getIdToken();
    const response = await fetch(
      `${import.meta.env.VITE_MONITORING_DASHBOARD_API_URL}/auth/sync-claims`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${firebaseToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ msIdToken }),
      }
    );
    if (!response.ok) {
      const body = await response.text().catch(() => '');
      throw new Error(`sync-claims failed: ${response.status} ${body}`);
    }
    await result.user.getIdToken(true);
  } catch (error) {
    console.error('Error syncing claims:', error);
  }
}

export function logout(): Promise<void> {
  return signOut(auth);
}

export function sendForgetPassword(email: string) {
  return sendPasswordResetEmail(auth, email);
}

export function resetPassword(oobCode: string, newPassword: string) {
  return confirmPasswordReset(auth, oobCode, newPassword);
}
